// LivestreamDetailOnlineFooterDesktop.jsx
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import LiveStreamSettingButton from '../container/LiveStreamSettingButton.js';
import VolumeController from '../container/VolumeController.js';

import { ButtonId } from '../resource/mixpanel.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import {
  PRESET_PREVIEW,
  privateShowStatus as privateShowConstants,
} from '../resource/liveStreamConstants.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import { color } from '../style/variables.js';

import GiftIconSrc from '../../img/icon-gift.png';
import ToyCommandIconSrc from '../../img/icon-toy-command.png';
import PrivateCtaIconSrc from '../../img/icon-private-cta.png';
import TheaterModeIconSrc from '../../img/ic-theater-mode.svg';

const TRIGGER_PRIVATE_BUTTON_MOUNT_ID =
  'trigger-private-button-livestream-detail-online-footer-desktop';
const GIFT_BUTTON_MOUNT_ID =
  'gift-button-livestream-detail-online-footer-desktop';
const KARAOKE_BUTTON_MOUNT_ID =
  'karaoke-button-livestream-detail-online-footer-desktop';
const SETTING_BUTTON_MOUNT_ID = 'livestream-setting-button-desktop';

export const LivestreamDetailOnlineFooterDesktop = ({
  streamId = null,
  streamStatus = null,
  isAsideExpanded = true,
  isStreamActivated = false,
  isTriggerPrivateModalOpen = false,
  isGiftModalOpen = false,
  isKaraokeModalOpen = false,
  hasStreamToken = false,
  needStreamTokenForExclusivePrivatePerforming = false,
  privateShowStatus = null,
  isKaraokeDeviceOnline = false,
  isExclusivePrivateOngoing = false,
  isRenderSlider = false,
  setAsideExpended = NULL_FUNCTION,
  toggleModal = NULL_FUNCTION,
}) => {
  const isShouldDisplayPrivateCta = useMemo(
    () =>
      (streamStatus === PRESET_PREVIEW || hasStreamToken) &&
      !isAsideExpanded &&
      !isExclusivePrivateOngoing,
    [hasStreamToken, isAsideExpanded, isExclusivePrivateOngoing, streamStatus]
  );

  const toggleDesktopAside = useCallback(() => {
    setAsideExpended({ isAsideExpanded: !isAsideExpanded });
  }, [setAsideExpended, isAsideExpanded]);
  const toggleKaraokeModal = useCallback(() => {
    if (!isStreamActivated) return;
    toggleModal({
      modalId: 'LiveStreamKaraokeModal',
      payload: {
        streamId,
        mountElementId: isStreamActivated ? KARAOKE_BUTTON_MOUNT_ID : '',
      },
      isOpen: !isKaraokeModalOpen,
    });
  }, [isStreamActivated, isKaraokeModalOpen, toggleModal, streamId]);
  const toggleTriggerPrivateModal = useCallback(() => {
    if (!isStreamActivated) return;
    toggleModal({
      modalId: 'LiveStreamTriggerPrivateModal',
      payload: {
        streamId,
        mountElementId: TRIGGER_PRIVATE_BUTTON_MOUNT_ID,
      },
      isOpen: !isTriggerPrivateModalOpen,
    });
  }, [isTriggerPrivateModalOpen, isStreamActivated, toggleModal, streamId]);
  const toggleGiftModal = useCallback(() => {
    toggleModal({
      modalId: 'LiveStreamGiftModal',
      payload: {
        streamId,
        mountElementId: GIFT_BUTTON_MOUNT_ID,
      },
      isOpen: !isGiftModalOpen,
    });
  }, [isGiftModalOpen, toggleModal, streamId]);

  const renderToyCommandButton = useCallback(() => {
    const shouldStopKaraokeIfPerforming = [
      privateShowConstants.PERFORMING,
      privateShowConstants.PREPARING,
    ].includes(privateShowStatus);

    const shouldHideToyButton =
      (shouldStopKaraokeIfPerforming && !isKaraokeDeviceOnline) ||
      isAsideExpanded;

    return (
      <ToyCommandContainer $shouldHide={shouldHideToyButton}>
        <ToyCommandWrapper>
          <ToyCommandButton
            onClick={toggleKaraokeModal}
            $isActive={isKaraokeModalOpen}
            data-element_id={ButtonId.LiveStream.ButtonCommandList}
            data-sheet_mount_id={
              isStreamActivated ? KARAOKE_BUTTON_MOUNT_ID : ''
            }
          >
            <ToyCommandIconContainer>
              <img src={ToyCommandIconSrc} alt="Toy Command" />
            </ToyCommandIconContainer>
          </ToyCommandButton>
        </ToyCommandWrapper>
      </ToyCommandContainer>
    );
  }, [
    isAsideExpanded,
    isKaraokeDeviceOnline,
    isKaraokeModalOpen,
    isStreamActivated,
    privateShowStatus,
    toggleKaraokeModal,
  ]);

  return (
    <StyledLivestreamDetailOnlineFooterDesktop>
      <LivestreamDetailLeftControlButtons>
        <VolumeControlWrapper>
          <VolumeController isRenderSlider={isRenderSlider} />
        </VolumeControlWrapper>
      </LivestreamDetailLeftControlButtons>

      <LivestreamDetailRightControlButtons>
        {/* Gift */}
        {!needStreamTokenForExclusivePrivatePerforming && !isAsideExpanded && (
          <GiftButton
            $isActive={isGiftModalOpen}
            data-element_id={ButtonId.All.ButtonGift}
            data-sheet_mount_id={isStreamActivated ? GIFT_BUTTON_MOUNT_ID : ''}
            onClick={toggleGiftModal}
          >
            <img src={GiftIconSrc} alt="Gift" />
          </GiftButton>
        )}
        {/* Private CTA */}
        {isShouldDisplayPrivateCta && (
          <PrivateCtaButton
            $isActive={isTriggerPrivateModalOpen}
            data-element_id={ButtonId.LiveStream.ButtonUserTrigger}
            data-sheet_mount_id={
              isStreamActivated ? TRIGGER_PRIVATE_BUTTON_MOUNT_ID : ''
            }
            onClick={toggleTriggerPrivateModal}
          >
            <img src={PrivateCtaIconSrc} alt="Private CTA" />
          </PrivateCtaButton>
        )}

        {/* Toy Command */}
        {!isAsideExpanded && renderToyCommandButton()}
        {isStreamActivated && (
          <LiveStreamSettingButton
            streamId={streamId}
            isActive={isStreamActivated}
            mountElementId={isStreamActivated ? SETTING_BUTTON_MOUNT_ID : ''}
          />
        )}
        <ControlButton
          data-element_id={
            isAsideExpanded
              ? ButtonId.All.ButtonCloseSidebar
              : ButtonId.All.ButtonOpenSidebar
          }
          onClick={toggleDesktopAside}
          title={isAsideExpanded ? 'Close sidebar' : 'Open sidebar'}
        >
          <img src={TheaterModeIconSrc} alt="toggle sidebar" />
        </ControlButton>
      </LivestreamDetailRightControlButtons>
    </StyledLivestreamDetailOnlineFooterDesktop>
  );
};

LivestreamDetailOnlineFooterDesktop.propTypes = {
  streamId: PropTypes.string,
  streamStatus: PropTypes.string,
  isAsideExpanded: PropTypes.bool,
  isStreamActivated: PropTypes.bool,
  isTriggerPrivateModalOpen: PropTypes.bool,
  isGiftModalOpen: PropTypes.bool,
  hasStreamToken: PropTypes.bool,
  needStreamTokenForExclusivePrivatePerforming: PropTypes.bool,
  isKaraokeModalOpen: PropTypes.bool,
  setAsideExpended: PropTypes.func,
  toggleModal: PropTypes.func,
  privateShowStatus: PropTypes.string,
  isKaraokeDeviceOnline: PropTypes.bool,
  isExclusivePrivateOngoing: PropTypes.bool,
  isRenderSlider: PropTypes.bool,
};

const StyledLivestreamDetailOnlineFooterDesktop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 16px;

  ${media.tablet`
    display: none;
  `}
`;

const LivestreamDetailLeftControlButtons = styled.div``;

const LivestreamDetailRightControlButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
`;

const ControlButton = styled.button`
  ${ResetButtonStyle};
  min-width: 40px;
  min-height: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(25, 25, 25, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  > img {
    object-fit: contain;
  }
`;

const VolumeControlWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const GiftButton = styled(ControlButton)`
  position: relative;
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 24px;
    height: 24px;
  }
`;

const PrivateCtaButton = styled(ControlButton)`
  position: relative;
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 24px;
    height: 24px;
  }
`;

const ToyCommandButton = styled(ControlButton)`
  position: relative;
  z-index: 2;
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 24px;
    height: 24px;
  }
`;

const ToyCommandContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${props =>
    props.$shouldHide &&
    css`
      display: none;
    `}
`;

const ToyCommandWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
`;

const ToyCommandIconContainer = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  will-change: transform;
  transform: translateZ(0);
  z-index: 2;

  > img {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  > img:first-child {
    /* ToyCommand icon */
    opacity: 1;
    transform: translate(-50%, -50%);
    will-change: transform, opacity;
  }
`;

export default LivestreamDetailOnlineFooterDesktop;
